import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

// Band visuals — no red anywhere.
const BANDS = {
  'needs-detail': {
    badge: 'bg-muted text-muted-foreground border-border',
    fill: 'bg-muted-foreground/40',
    width: 'w-1/4',
  },
  'taking-shape': {
    badge: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
    fill: 'bg-amber-400',
    width: 'w-1/2',
  },
  'looking-good': {
    badge: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800',
    fill: 'bg-green-500',
    width: 'w-3/4',
  },
  'vault-ready': {
    badge: 'bg-primary/10 text-primary border-primary/20',
    fill: 'bg-primary',
    width: 'w-full',
  },
};

const Header = ({ badge }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Shield className="w-5 h-5 text-primary" />
      <h3 className="font-heading font-medium text-foreground">Vault Health</h3>
    </div>
    {badge}
  </div>
);

const VaultHealthCard = ({ data, loading, error }) => {
  // Animate the fill in from zero on first paint.
  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => {
    if (data?.band) {
      const t = setTimeout(() => setAnimateIn(true), 50);
      return () => clearTimeout(t);
    }
  }, [data?.band]);

  if (loading) {
    return (
      <Card className="border border-border" data-testid="vault-health-card">
        <CardContent className="p-6 space-y-4">
          <Header badge={<div className="h-6 w-28 rounded-full bg-muted animate-pulse" />} />
          <div className="h-2 w-full rounded-full bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.band) {
    return (
      <Card className="border border-border" data-testid="vault-health-card">
        <CardContent className="p-6 space-y-3">
          <Header badge={null} />
          <p className="text-sm text-muted-foreground">Unable to load vault health right now.</p>
        </CardContent>
      </Card>
    );
  }

  const cfg = BANDS[data.band] || BANDS['needs-detail'];
  const recommendations = data.recommendations || [];

  // A brand-new vault (no progress in any section) shows no fill at all.
  const b = data.breakdown || {};
  const isCompletelyEmpty =
    (b.depth?.score || 0) === 0 &&
    (b.continuity?.score || 0) === 0 &&
    (b.formal?.score || 0) === 0;

  const barWidth = isCompletelyEmpty
    ? 'w-0'
    : data.band === 'needs-detail' ? 'w-1/4'
    : data.band === 'taking-shape' ? 'w-1/2'
    : data.band === 'looking-good' ? 'w-3/4'
    : 'w-full';

  const message = isCompletelyEmpty
    ? 'Start by adding your contacts and first map entry - your vault health will grow from there.'
    : data.message;

  return (
    <Card className="border border-border" data-testid="vault-health-card">
      <CardContent className="p-6">
        <Header
          badge={
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.badge}`} data-testid="vault-health-band">
              {data.label}
            </span>
          }
        />

        {/* Progress bar (band-based, not numeric) */}
        <div className="mt-4 h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ease-out ${cfg.fill} ${animateIn ? barWidth : 'w-0'}`}
          />
        </div>

        {/* Message */}
        <p className="text-sm text-muted-foreground mt-3">{message}</p>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-2">To improve:</p>
            <div className="flex flex-wrap gap-2">
              {recommendations.map((rec, i) => (
                <Link
                  key={i}
                  to={rec.link}
                  className="inline-flex items-center gap-1.5 bg-muted hover:bg-primary/10 rounded-full px-3 py-1 text-xs transition-colors"
                  data-testid={`vault-health-rec-${i}`}
                >
                  <span>{rec.text}</span>
                  <ArrowRight className="w-3 h-3 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VaultHealthCard;
